import {Component, EventEmitter, Input, OnChanges, Output, TemplateRef, ViewChild} from '@angular/core';
import {KeyValuePair} from '@app/cart/dcp-cart-configuration.model';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {ArrayService, DcpEntryGroup, DcpPriceData} from '@dcp/configurator';
import {DefaultModalService} from '@app/shared/modal/services/default-modal.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {first} from 'rxjs';
import {TranslationService} from '@spartacus/core';

export interface AdditionalSolution {
  products: OrderEntry[];
  derivatives: OrderEntry[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-additional-required-solutions-modal',
  styleUrl: './additional-required-solutions-modal.component.scss',
  templateUrl: './additional-required-solutions-modal.component.html',
})
export class AdditionalRequiredSolutionsModalComponent implements OnChanges {

  @Input() isModalOpened: boolean;
  @Input() derivativePrice: string;
  @Output() isModalOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  /** orderEntriesOneParent can be set directly in case contractGroup cannot be used */
  @Input() orderEntriesOneParent: AdditionalSolution[] = [];
  @Input() priceData: DcpPriceData;
  orderEntriesSomeParents: AdditionalSolution[] = [];
  orderEntriesAllParents: AdditionalSolution[] = [];
  modalTitleKey = 'checkout.additionalSolutions';
  modalTitleValue: string;

  @Input() set contractGroup(contractGroup: DcpEntryGroup) {
    if (contractGroup) {
      const uniqDerivativeIds: string[] = this.uniqueDerivativeIds(contractGroup.entryGroups);
      for (const derivativeId of uniqDerivativeIds) {
        const parentEntryGroups: DcpEntryGroup[] = this.entryGroupService.getRootGroupsBySupplierAlternativeId(contractGroup.entryGroups, derivativeId);
        const splitGroupsByDates: KeyValuePair<DcpEntryGroup[]> = this.groupByDates(parentEntryGroups);
        for (const parentEntryGroupsWithSameDate of Object.values(splitGroupsByDates)) {
          this.addOrderEntries(parentEntryGroupsWithSameDate, contractGroup, derivativeId);
        }
      }
    }
  }

  @ViewChild('modalContentTemplate') modalContentTemplate: TemplateRef<any>;

  constructor(
    private entryGroupService: EntryGroupService,
    private dcpOrderEntryService: DcpOrderEntryService,
    private modalService: DefaultModalService,
    private translationService: TranslationService,
  ) {
  }

  ngOnChanges(): void {
    if (this.isModalOpened) {
      setTimeout(() => {
        this.openModal();
      });
    }
  }

  openModal() {
    this.translationService.translate(this.modalTitleKey)
      .pipe(first(), untilDestroyed(this))
      .subscribe((translation) => {
        this.modalTitleValue = translation;
    });
    this.modalService.openModal(this.modalContentTemplate, this.modalTitleValue);
    this.modalService.onClose().pipe(first(), untilDestroyed(this)).subscribe(() => {
      this.closeModal();
    });
  }

  closeModal() {
    this.isModalOpened = false;
    this.isModalOpenedChange.emit(this.isModalOpened);
  }

  getDateId(orderEntry: OrderEntry): string {
    const configuration = this.dcpOrderEntryService.getConfiguration(orderEntry);
    return JSON.stringify({startDate: configuration.startDate, endDate: configuration.endDate});
  }

  /**
   * Groups the parentEntryGroups in an object where the keys are a json string:
   * {startDate: configuration.startDate, endDate: configuration.endDate}
   *
   * @param parentEntryGroups DcpEntryGroup[] to group
   */
  groupByDates(parentEntryGroups: DcpEntryGroup[]): KeyValuePair<DcpEntryGroup[]> {
    return parentEntryGroups.reduce((collection, entryGroup) => {
      const entries: OrderEntry[] = this.entryGroupService.getOrderEntriesByBundleType([entryGroup]);
      if (entries?.length && entries[0]) {
        const key: string = this.getDateId(entries[0]);
        (collection[key] = collection[key] || []).push(entryGroup);
      }
      return collection;
    }, {});
  }

  uniqueDerivativeIds(entryGroups: DcpEntryGroup[]): string[] {
    const allDerivatives: DcpEntryGroup[] = this.entryGroupService.collectEntryGroups(this.entryGroupService.isDerivative, entryGroups, []);
    const derivativeIds: string[][] = allDerivatives.map(ad => ad.entries?.map(e => e.product?.supplierAlternativeAID));
    const flatDerivativeIds: string[] = [].concat.apply([], derivativeIds);
    return ArrayService.removeDuplicates(flatDerivativeIds).filter(e => !!e);
  }

  private getAdditionalSolutionDerivatives(parentEntryGroups: DcpEntryGroup[], derivativeId: string): OrderEntry {
    const derivativeEntries: OrderEntry[] = this.entryGroupService.getOrderEntriesByBundleType(parentEntryGroups, this.entryGroupService.isDerivative);
    return derivativeEntries.find(e => e.product?.supplierAlternativeAID === derivativeId);
  }

  private addOrderEntries(parentEntryGroupsWithSameDate: DcpEntryGroup[], contractGroup: DcpEntryGroup, derivativeId: string) {
    if (parentEntryGroupsWithSameDate.length === 1) {
      this.addToOrderEntriesOneParent(parentEntryGroupsWithSameDate, derivativeId);
    } else {
      if (parentEntryGroupsWithSameDate.length !== contractGroup.entryGroups.length) {
        this.addToOrderEntriesSomeParent(parentEntryGroupsWithSameDate, derivativeId);
      } else {
        this.addToOrderEntriesAllParent(parentEntryGroupsWithSameDate, derivativeId);
      }
    }
  }

  /**
   * Adds the derivative to this.orderEntriesOneParent if it's not present already.
   *
   * @param parentEntryGroups all parentEntryGroups of the current derivative
   * @param derivativeId current derivativeId
   */
  addToOrderEntriesOneParent(parentEntryGroups: DcpEntryGroup[], derivativeId: string) {
    const products: OrderEntry[] = this.entryGroupService.getOrderEntriesByBundleType(parentEntryGroups);
    const productId: string = products[0].product.supplierAlternativeAID;
    const productDateId: string = this.getDateId(products[0]);
    const presentEntry: AdditionalSolution = this.orderEntriesOneParent.find(
      oe => oe.products.some(p => p.product.supplierAlternativeAID === productId && this.getDateId(p) === productDateId)
    );
    if (presentEntry) {
      presentEntry.derivatives.push(
        this.getAdditionalSolutionDerivatives(parentEntryGroups, derivativeId)
      );
    } else {
      this.orderEntriesOneParent.push({
        products: [this.entryGroupService.getFirstEntry(parentEntryGroups)],
        derivatives: [this.getAdditionalSolutionDerivatives(parentEntryGroups, derivativeId)],
      });
    }
  }

  /**
   * Adds the derivative to this.orderEntriesSomeParents if it's not present already.
   *
   * @param parentEntryGroups all parentEntryGroups of the current derivative
   * @param derivativeId current derivativeId
   */
  addToOrderEntriesSomeParent(parentEntryGroups: DcpEntryGroup[], derivativeId: string) {
    const presentEntry: AdditionalSolution = this.getPresentEntriesByDerivativeId(this.orderEntriesSomeParents, derivativeId);
    const editionOrderEntries: OrderEntry[] = this.entryGroupService.getOrderEntriesByBundleType(parentEntryGroups);
    if (presentEntry) {
      presentEntry.products = presentEntry.products.concat(editionOrderEntries);
    } else {
      this.orderEntriesSomeParents.push({
        products: editionOrderEntries,
        derivatives: [this.getAdditionalSolutionDerivatives(parentEntryGroups, derivativeId)],
      });
    }
  }

  /**
   * Adds the derivative to this.orderEntriesSomeParents if it's not present already.
   *
   * @param parentEntryGroups all parentEntryGroups of the current derivative
   * @param derivativeId current derivativeId
   */
  addToOrderEntriesAllParent(parentEntryGroups: DcpEntryGroup[], derivativeId: string) {
    if (this.orderEntriesAllParents.length) {
      this.orderEntriesAllParents[0].derivatives.push(
        this.getAdditionalSolutionDerivatives(parentEntryGroups, derivativeId)
      );
    } else {
      this.orderEntriesAllParents.push({
        // A static text is displayed instead of the Products => Products are not collected.
        products: [],
        derivatives: [this.getAdditionalSolutionDerivatives(parentEntryGroups, derivativeId)],
      });
    }
  }

  getPresentEntriesByDerivativeId(additionalSolution: AdditionalSolution[], derivativeId): AdditionalSolution {
    return additionalSolution.find(oe => oe.derivatives.some(p => p.product.supplierAlternativeAID === derivativeId));
  }
}
